<template>
  <div style="max-width: 90%; margin: auto" class="py-10">
    <!-- Header -->
    <div class="mb-8">
      <p class="page-subtitle mb-1">Verwaltung</p>
      <h1 class="page-title">Integrationen</h1>
    </div>

    <!-- GitHub Card -->
    <div class="card-modern mb-4">
      <div class="card-head">
        <div class="d-flex align-center" style="gap: 14px">
          <div class="gh-logo">
            <v-icon size="26" color="white">mdi-github</v-icon>
          </div>
          <div>
            <div class="service-name">GitHub</div>
            <div class="service-desc">Verknüpfe ein Repository, um Sections, Snippets, Assets und Blocks zu synchronisieren</div>
          </div>
        </div>
        <span v-if="!loading" class="status-pill" :class="status.connected ? 'status-connected' : 'status-disconnected'">
          <span class="status-dot" :class="status.connected ? 'dot-on' : 'dot-off'"></span>
          {{ status.connected ? 'Verbunden' : 'Nicht verbunden' }}
        </span>
      </div>

      <div v-if="loading" class="skel-block mt-4"></div>

      <template v-else>
        <!-- NICHT VERBUNDEN -->
        <div v-if="!status.connected" class="mt-5">
          <div class="info-banner mb-4">
            <v-icon size="16" color="#5B5BD6">mdi-information-outline</v-icon>
            <div>
              <strong>Fine-grained Personal Access Token</strong> auf GitHub erstellen
              (<a href="https://github.com/settings/personal-access-tokens/new" target="_blank">Settings → Developer Settings → PATs</a>)
              mit <strong>Contents: Read &amp; Write</strong>-Berechtigung für das Ziel-Repo. Token hier einfügen.
            </div>
          </div>

          <div class="fg mb-3">
            <label class="fl">GitHub Personal Access Token *</label>
            <div class="iw" :class="{ 'iw-f': focusField === 'token' }">
              <v-icon size="15" color="rgba(0,0,0,0.3)">mdi-key-outline</v-icon>
              <input
                v-model="tokenInput"
                :type="showToken ? 'text' : 'password'"
                class="ii"
                placeholder="github_pat_…"
                :disabled="connecting"
                @focus="focusField = 'token'"
                @blur="focusField = null"
              />
              <v-icon
                size="15"
                color="#888"
                style="cursor: pointer"
                @click="showToken = !showToken"
              >{{ showToken ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}</v-icon>
            </div>
          </div>

          <div v-if="connectMessage" class="alert-error mb-3">
            <v-icon size="16" color="#D32F2F">mdi-alert-circle-outline</v-icon>{{ connectMessage }}
          </div>

          <button class="submit-btn" :disabled="!tokenInput.trim() || connecting" @click="connectGithub">
            <v-progress-circular v-if="connecting" indeterminate size="14" width="2" color="white" />
            <v-icon v-else size="16">mdi-link-variant</v-icon>
            <span>Mit GitHub verbinden</span>
          </button>
        </div>

        <!-- VERBUNDEN -->
        <div v-else class="mt-5">
          <div class="user-box mb-4">
            <v-avatar size="36" class="user-avatar">
              <v-img v-if="status.user?.avatar" :src="status.user.avatar" cover>
                <template #error><v-icon size="18" color="#888">mdi-github</v-icon></template>
              </v-img>
              <v-icon v-else size="18" color="#888">mdi-github</v-icon>
            </v-avatar>
            <div class="flex-grow-1" style="min-width: 0">
              <div class="user-label">Verbunden als</div>
              <div class="user-name">{{ status.user?.login || '—' }}</div>
            </div>
            <button class="danger-btn" @click="disconnectGithub" :disabled="disconnecting">
              <v-progress-circular v-if="disconnecting" indeterminate size="14" width="2" color="#D32F2F" />
              <v-icon v-else size="15">mdi-link-variant-off</v-icon>
              <span>Trennen</span>
            </button>
          </div>

          <div class="divider-soft mb-4"></div>

          <!-- Repo Auswahl -->
          <div class="d-flex justify-space-between align-center mb-3 flex-wrap" style="gap: 10px">
            <div>
              <div class="section-title">Verknüpftes Repository</div>
              <div class="section-sub">Wähle das Shopify-Theme-Repo aus, mit dem synchronisiert werden soll</div>
            </div>
            <button class="secondary-btn" :disabled="loadingRepos" @click="loadRepos">
              <v-progress-circular v-if="loadingRepos" indeterminate size="14" width="2" color="#0a1628" />
              <v-icon v-else size="15">mdi-refresh</v-icon>
              <span>Repos laden</span>
            </button>
          </div>

          <div v-if="status.repo" class="repo-active mb-4">
            <v-icon size="20" color="#2E7D32">mdi-source-repository</v-icon>
            <div class="flex-grow-1" style="min-width: 0">
              <div class="repo-active-label">Aktuell verknüpft</div>
              <div class="repo-active-name">{{ status.repo.fullName }}</div>
              <div class="repo-active-branch">
                <v-icon size="11">mdi-source-branch</v-icon>
                {{ status.repo.branch }}
              </div>
            </div>
          </div>

          <div v-if="repos.length > 0 && !manualMode" class="fg mb-3" ref="repoDropdown">
            <label class="fl">Repository {{ status.repo ? 'ändern' : 'auswählen' }}</label>
            <button
              type="button"
              class="iw iw-trigger"
              :class="{ 'iw-f': repoMenuOpen }"
              @click="repoMenuOpen = !repoMenuOpen"
            >
              <v-icon size="15" color="rgba(0,0,0,0.3)">mdi-source-repository</v-icon>
              <span class="ii-val">{{ selectedRepo ? `${selectedRepo.fullName} · ${selectedRepo.defaultBranch}` : 'Repository wählen…' }}</span>
              <v-icon size="14" color="#888" :class="{ 'chev-open': repoMenuOpen }">mdi-chevron-down</v-icon>
            </button>
            <div v-if="repoMenuOpen" class="combo-menu">
              <button
                v-for="r in repos"
                :key="r.id"
                type="button"
                class="combo-option"
                @click="selectRepo(r)"
              >
                <v-icon v-if="r.private" size="12" color="#E65100">mdi-lock-outline</v-icon>
                <v-icon v-else size="12" color="#2E7D32">mdi-source-repository</v-icon>
                <div class="combo-repo-body">
                  <div class="combo-repo-name">{{ r.fullName }}</div>
                  <div class="combo-repo-sub">Branch: {{ r.defaultBranch }}{{ r.description ? ' · ' + r.description : '' }}</div>
                </div>
                <v-icon v-if="selectedRepo?.id === r.id" size="14" color="#2E7D32" style="margin-left: auto">mdi-check</v-icon>
              </button>
            </div>
          </div>

          <!-- Empty-State + Manual-Entry Fallback -->
          <div v-if="repos.length === 0 && !loadingRepos && !manualMode" class="info-banner mb-3" style="background: rgba(255,152,0,0.08); color: #8A4100">
            <v-icon size="16" color="#E65100">mdi-information-outline</v-icon>
            <div>
              <strong>Keine Repos via API gefunden.</strong> Das liegt bei Fine-Grained PATs oft daran, dass der Token nur auf einzelne Repos beschränkt ist. Du kannst das Repo einfach manuell eingeben — funktioniert genauso.
              <button class="link-btn mt-2" @click="manualMode = true">
                <v-icon size="14">mdi-pencil-outline</v-icon> Repo manuell eingeben
              </button>
            </div>
          </div>

          <!-- Manual Mode -->
          <div v-if="manualMode">
            <div class="fg mb-3">
              <label class="fl">Repository <span class="fl-hint">(Format: owner/name)</span></label>
              <div class="iw" :class="{ 'iw-f': focusField === 'manual' }">
                <v-icon size="15" color="rgba(0,0,0,0.3)">mdi-source-repository</v-icon>
                <input
                  v-model="manualRepo"
                  class="ii"
                  placeholder="z.B. floow-media/shopify-theme"
                  @focus="focusField = 'manual'"
                  @blur="focusField = null"
                />
              </div>
            </div>
            <div class="fg mb-3" style="max-width: 300px">
              <label class="fl">Branch <span class="fl-hint">(optional, Default ermittelt sich auto)</span></label>
              <div class="iw" :class="{ 'iw-f': focusField === 'branch' }">
                <v-icon size="15" color="rgba(0,0,0,0.3)">mdi-source-branch</v-icon>
                <input
                  v-model="manualBranch"
                  class="ii"
                  placeholder="main"
                  @focus="focusField = 'branch'"
                  @blur="focusField = null"
                />
              </div>
            </div>
            <div class="d-flex mt-3" style="gap: 10px">
              <button class="submit-btn" :disabled="!manualRepo.includes('/') || settingRepo" @click="saveRepoManual">
                <v-progress-circular v-if="settingRepo" indeterminate size="14" width="2" color="white" />
                <v-icon v-else size="16">mdi-check</v-icon>
                <span>Repository verknüpfen</span>
              </button>
              <button v-if="repos.length > 0" class="secondary-btn" @click="manualMode = false">
                <v-icon size="15">mdi-arrow-left</v-icon>
                <span>Zurück zur Liste</span>
              </button>
            </div>
          </div>

          <button
            v-if="!manualMode && selectedRepo && (!status.repo || status.repo.fullName !== selectedRepo.fullName)"
            class="submit-btn"
            :disabled="settingRepo"
            @click="saveRepo"
          >
            <v-progress-circular v-if="settingRepo" indeterminate size="14" width="2" color="white" />
            <v-icon v-else size="16">mdi-check</v-icon>
            <span>Repository verknüpfen</span>
          </button>

          <div v-if="reposMessage" class="mt-3 alert-error">
            <v-icon size="16" color="#D32F2F">mdi-alert-circle-outline</v-icon>{{ reposMessage }}
          </div>
        </div>
      </template>
    </div>

    <!-- Info Card -->
    <div v-if="status.connected && status.repo" class="card-modern info-card">
      <div class="d-flex align-center mb-3" style="gap: 10px">
        <div class="title-icon"><v-icon size="16" color="#5B5BD6">mdi-sync</v-icon></div>
        <div class="section-title">Wie funktioniert die Synchronisation?</div>
      </div>
      <ul class="info-list">
        <li>Auf der <strong>Section-Detail-Seite</strong> findest du bei jeder Datei (Liquid, Snippets, Assets, Blocks) einen „Sync"-Button.</li>
        <li>Existiert die Datei im Repo <strong>nicht</strong>, wird sie angelegt.</li>
        <li>Bei Unterschieden fragt SAM, in welche Richtung synchronisiert werden soll: <strong>SAM → Git</strong> oder <strong>Git → SAM</strong>.</li>
        <li>Wird eine Datei in SAM gelöscht, kann sie auf Wunsch auch im Repo entfernt werden.</li>
      </ul>
    </div>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor === 'success' ? '#2E7D32' : '#D32F2F'"
      timeout="3000"
      location="top"
    >
      <div class="d-flex align-center" style="gap: 8px">
        <v-icon size="18" color="white">{{ snackbarColor === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
        {{ snackbarMessage }}
      </div>
    </v-snackbar>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'IntegrationsOverview',

  data() {
    return {
      loading: true,
      status: { connected: false, user: null, repo: null },

      tokenInput: '',
      showToken: false,
      connecting: false,
      connectMessage: '',

      disconnecting: false,

      repos: [],
      loadingRepos: false,
      reposMessage: '',
      repoMenuOpen: false,
      selectedRepo: null,
      settingRepo: false,
      manualMode: false,
      manualRepo: '',
      manualBranch: '',

      focusField: null,

      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
    };
  },

  mounted() {
    this.loadStatus();
    document.addEventListener('click', this.handleOutsideClick);
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
  },

  methods: {
    handleOutsideClick(e) {
      if (this.repoMenuOpen) {
        const el = this.$refs.repoDropdown;
        if (el && !el.contains(e.target)) this.repoMenuOpen = false;
      }
    },

    showSnackbar(message, color = 'success') {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.snackbar = true;
    },

    async loadStatus() {
      this.loading = true;
      try {
        const { data } = await axios.get('/api/integrations/github/status');
        this.status = data || { connected: false };
        if (this.status.connected) {
          this.selectedRepo = this.status.repo ? {
            id: `${this.status.repo.owner}/${this.status.repo.name}`,
            fullName: this.status.repo.fullName,
            owner: this.status.repo.owner,
            name: this.status.repo.name,
            defaultBranch: this.status.repo.branch,
          } : null;
          await this.loadRepos();
        }
      } catch (e) {
        console.error('Status error:', e);
      } finally {
        this.loading = false;
      }
    },

    async connectGithub() {
      this.connecting = true;
      this.connectMessage = '';
      try {
        const { data } = await axios.post('/api/integrations/github/connect', {
          token: this.tokenInput,
          connectedBy: this.$user?.id || null,
        });
        if (data.success) {
          this.tokenInput = '';
          this.showToken = false;
          this.showSnackbar('Mit GitHub verbunden');
          await this.loadStatus();
        } else {
          this.connectMessage = data.message || 'Fehler beim Verbinden';
        }
      } catch (e) {
        this.connectMessage = e.response?.data?.message || 'Token ungültig oder keine Berechtigung';
      } finally {
        this.connecting = false;
      }
    },

    async disconnectGithub() {
      if (!confirm('GitHub wirklich trennen? Das Repo bleibt unberührt, aber Sync ist danach nicht mehr möglich.')) return;
      this.disconnecting = true;
      try {
        await axios.post('/api/integrations/github/disconnect');
        this.status = { connected: false };
        this.repos = [];
        this.selectedRepo = null;
        this.showSnackbar('GitHub getrennt');
      } catch (e) {
        this.showSnackbar(e.response?.data?.message || 'Fehler beim Trennen', 'error');
      } finally {
        this.disconnecting = false;
      }
    },

    async loadRepos() {
      this.loadingRepos = true;
      this.reposMessage = '';
      try {
        const { data } = await axios.get('/api/integrations/github/repos');
        if (data.success) {
          this.repos = data.data || [];
        } else {
          this.reposMessage = data.message || 'Fehler beim Laden der Repos';
        }
      } catch (e) {
        this.reposMessage = e.response?.data?.message || 'Fehler beim Laden der Repos';
      } finally {
        this.loadingRepos = false;
      }
    },

    selectRepo(r) {
      this.selectedRepo = r;
      this.repoMenuOpen = false;
    },

    async saveRepo() {
      if (!this.selectedRepo) return;
      this.settingRepo = true;
      try {
        await axios.post('/api/integrations/github/set-repo', {
          owner: this.selectedRepo.owner,
          name: this.selectedRepo.name,
          branch: this.selectedRepo.defaultBranch,
        });
        this.showSnackbar(`${this.selectedRepo.fullName} verknüpft`);
        await this.loadStatus();
      } catch (e) {
        this.showSnackbar(e.response?.data?.message || 'Fehler beim Verknüpfen', 'error');
      } finally {
        this.settingRepo = false;
      }
    },

    async saveRepoManual() {
      const input = (this.manualRepo || '').trim().replace(/^https?:\/\/github\.com\//, '').replace(/\.git$/, '');
      const parts = input.split('/').filter(Boolean);
      if (parts.length < 2) {
        this.showSnackbar('Bitte im Format owner/name eingeben', 'error');
        return;
      }
      const [owner, name] = parts;
      this.settingRepo = true;
      try {
        const { data } = await axios.post('/api/integrations/github/set-repo', {
          owner,
          name,
          branch: this.manualBranch.trim() || null,
        });
        if (!data.success) throw new Error(data.message);
        this.showSnackbar(`${owner}/${name} verknüpft`);
        this.manualMode = false;
        this.manualRepo = '';
        this.manualBranch = '';
        await this.loadStatus();
      } catch (e) {
        this.showSnackbar(e.response?.data?.message || e.message || 'Fehler beim Verknüpfen', 'error');
      } finally {
        this.settingRepo = false;
      }
    },
  },
};
</script>

<style scoped>
.page-title { font-size: 26px; font-weight: 800; color: #0a1628; letter-spacing: -0.3px; }
.page-subtitle { font-size: 13px; color: #888; }

.card-modern { background: white; border: 1px solid #eceef2; border-radius: 16px; padding: 28px; }
.info-card { background: #fafbfc; }

.card-head {
  display: flex; align-items: center; justify-content: space-between;
  gap: 16px; flex-wrap: wrap;
}

.gh-logo {
  width: 52px; height: 52px; min-width: 52px; border-radius: 14px;
  background: #0a1628; display: flex; align-items: center; justify-content: center;
}
.service-name { font-size: 18px; font-weight: 800; color: #0a1628; }
.service-desc { font-size: 13px; color: #888; margin-top: 2px; max-width: 560px; }

.status-pill {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 5px 12px; border-radius: 20px; font-size: 11px; font-weight: 700;
}
.status-connected { background: rgba(46,125,50,0.1); color: #2E7D32; }
.status-disconnected { background: #f0f2f5; color: #888; }
.status-dot { width: 6px; height: 6px; border-radius: 50%; }
.dot-on { background: #2E7D32; }
.dot-off { background: #bbb; }

.section-title { font-size: 14px; font-weight: 700; color: #0a1628; }
.section-sub { font-size: 12px; color: #888; margin-top: 2px; }

.title-icon {
  width: 30px; height: 30px; border-radius: 8px;
  background: rgba(91,91,214,0.1);
  display: flex; align-items: center; justify-content: center;
}

.divider-soft { height: 1px; background: #eceef2; }

/* User box */
.user-box {
  display: flex; align-items: center; gap: 14px;
  padding: 14px; background: #fafbfc; border: 1px solid #eceef2; border-radius: 12px;
}
.user-avatar { background: #e8eaed !important; }
.user-label { font-size: 11px; font-weight: 600; color: #999; text-transform: uppercase; letter-spacing: 0.3px; }
.user-name { font-size: 14px; font-weight: 700; color: #0a1628; margin-top: 2px; }

/* Repo active */
.repo-active {
  display: flex; align-items: center; gap: 14px;
  padding: 14px 16px; background: rgba(46,125,50,0.06);
  border: 1px solid rgba(46,125,50,0.2); border-radius: 12px;
}
.repo-active-label { font-size: 11px; font-weight: 600; color: #2E7D32; text-transform: uppercase; letter-spacing: 0.3px; }
.repo-active-name { font-size: 14px; font-weight: 700; color: #0a1628; margin-top: 2px; font-family: 'SFMono-Regular', Menlo, monospace; }
.repo-active-branch {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 11px; color: #555; margin-top: 4px;
  font-family: 'SFMono-Regular', Menlo, monospace;
}

/* Info banner */
.info-banner {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 12px 14px; background: rgba(91,91,214,0.08); color: #3A3AC0;
  border-radius: 10px; font-size: 13px; line-height: 1.55;
}
.info-banner a { color: #5B5BD6; text-decoration: none; font-weight: 600; }
.info-banner a:hover { text-decoration: underline; }

.link-btn {
  display: inline-flex; align-items: center; gap: 4px;
  background: transparent; border: none; padding: 0; cursor: pointer;
  color: #E65100; font-weight: 700; font-size: 13px; font-family: inherit;
}
.link-btn:hover { text-decoration: underline; }
.fl-hint { color: #bbb; font-weight: 400; text-transform: none; letter-spacing: 0; margin-left: 4px; }

.info-list { padding-left: 0; list-style: none; margin: 0; display: flex; flex-direction: column; gap: 8px; }
.info-list li {
  font-size: 13px; color: #555; line-height: 1.55;
  padding-left: 20px; position: relative;
}
.info-list li::before {
  content: ''; position: absolute; left: 4px; top: 9px;
  width: 6px; height: 6px; background: #5B5BD6; border-radius: 50%;
}

/* Form */
.fg { display: flex; flex-direction: column; position: relative; }
.fl { font-size: 11px; font-weight: 600; color: #999; margin-bottom: 6px; letter-spacing: 0.3px; }

.iw {
  display: flex; align-items: center; gap: 10px;
  height: 44px; padding: 0 14px; width: 100%;
  background: white; border: 1px solid #eceef2; border-radius: 10px;
  transition: all 0.15s;
}
.iw.iw-f { border-color: #0a1628; box-shadow: 0 0 0 3px rgba(10,22,40,0.04); }
.iw-trigger { cursor: pointer; text-align: left; font-family: inherit; background: white !important; }
.iw-trigger:hover:not(.iw-f) { border-color: #c5cbd3; }
.ii {
  flex: 1; border: none; outline: none; background: transparent;
  font-size: 13px; color: #0a1628; font-weight: 500; min-width: 0;
  font-family: inherit; height: 42px;
}
.ii::placeholder { color: #bbb; font-weight: 400; }
.ii-val { flex: 1; font-size: 13px; color: #0a1628; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.chev-open { transform: rotate(180deg); transition: transform 0.2s; }

.combo-menu {
  position: absolute; top: calc(100% + 6px); left: 0; right: 0;
  background: white; border: 1px solid #eceef2; border-radius: 10px;
  box-shadow: 0 12px 32px rgba(10,22,40,0.12);
  z-index: 50; max-height: 320px; overflow-y: auto;
  padding: 6px; animation: comboFade 0.12s ease;
}
@keyframes comboFade { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
.combo-option {
  display: flex; align-items: center; gap: 10px; width: 100%;
  padding: 9px 10px; background: transparent; border: none;
  border-radius: 7px; cursor: pointer; transition: background 0.1s;
  text-align: left; font-family: inherit;
}
.combo-option:hover { background: #f8f9fb; }
.combo-repo-body { flex: 1; min-width: 0; }
.combo-repo-name {
  font-size: 13px; font-weight: 600; color: #0a1628;
  font-family: 'SFMono-Regular', Menlo, monospace;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.combo-repo-sub {
  font-size: 11px; color: #888;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}

/* Buttons */
.submit-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  padding: 12px 20px; background: #0a1628; color: white; border: none; border-radius: 10px;
  font-size: 13px; font-weight: 700; cursor: pointer;
  transition: background 0.15s; font-family: inherit;
}
.submit-btn:hover:not(:disabled) { background: #1d3457; }
.submit-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.secondary-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 9px 14px; border: 1px solid #eceef2; border-radius: 10px;
  background: white; font-size: 12px; font-weight: 600; color: #0a1628;
  cursor: pointer; transition: border-color 0.15s; font-family: inherit;
}
.secondary-btn:hover:not(:disabled) { border-color: #0a1628; }
.secondary-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.danger-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 9px 14px; background: white; color: #D32F2F;
  border: 1px solid rgba(211,47,47,0.25); border-radius: 10px;
  font-size: 12px; font-weight: 600; cursor: pointer;
  transition: all 0.15s; font-family: inherit;
}
.danger-btn:hover:not(:disabled) { background: rgba(211,47,47,0.06); border-color: #D32F2F; }

.alert-error {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 14px; background: rgba(211,47,47,0.08); color: #D32F2F;
  border-radius: 10px; font-size: 13px; font-weight: 500;
}

/* Skeleton */
.skel-block {
  height: 80px; border-radius: 10px;
  background: linear-gradient(90deg, #eceef2 25%, #f5f6f8 50%, #eceef2 75%);
  background-size: 200% 100%; animation: shimmer 1.5s infinite;
}
@keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
</style>
